import {
  Box,
  Button,
  Container,
  ListItem,
  ListItemButton,
  Stack,
  SxProps,
  useTheme,
} from '@mui/material';

import { FC } from 'react';
import { ILocale } from '@/lang/types';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { WalletSidMenuAuthHint } from './wallet-side-menu-auth-hint';
import { checkIsRtl } from '@/lang/utils';
import { useRouter } from 'next/router';

interface WalletCashbackInfoBadgeProps {
  text: string;
  onBadgeClick: () => void;
  customSx?: SxProps;
  customImg?: string;
}
const WalletCashbackInfoBadge: FC<WalletCashbackInfoBadgeProps> = ({
  text,
  onBadgeClick,
  customSx,
  customImg,
}) => {
  const theme = useTheme();
  const router = useRouter();

  const isRtl = checkIsRtl(router.locale as ILocale);

  const ArrowIConSx: SxProps = {
    transition: '0.3s ease-out',
    color: theme.palette.grey[80],
    '&:hover': {
      transition: '0.3s ease-out',
      WebkitTransform: 'translateX(4px)',
      MozTransform: 'translateX(4px)',
      msTransform: 'translateX(4px)',
      OTransform: 'translateX(4px)',
      transform: 'translateX(4px)',
      WebkitTransition: 'transform 0.3s ease-out',
      MozTransition: 'transform 0.3s ease-out',
      msTransition: 'transform 0.3s ease-out',
      OTransition: 'transform 0.3s ease-out',
      color: 'white',
    },
  };

  return (
    <Container
      component='section'
      sx={{
        maxWidth: '1784px !important',
        paddingX: {
          mobile: '16px !important',
          largeTablet: '30px !important',
          largeDesktop: '60px !important',
        },
        marginBlockEnd: 10,
        ...customSx,
      }}
    >
      <ListItem
        key='wallet-cashback-badge'
        component='div'
        disableGutters
        sx={{
          paddingY: '10.5px',
          display: {
            mobile: 'flex',
            mediumDesktop: 'flex',
          },
        }}
      >
        <ListItemButton
          component='a'
          disableGutters
          sx={{
            borderRadius: '12px',
            paddingX: '16px',
            paddingY: '8px',
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
              backgroundColor: '#f2c3fb',
            },
          }}
          onClick={onBadgeClick}
        >
          <Stack
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              flexGrow: 1,
            }}
          >
            <WalletSidMenuAuthHint
              message={text}
              img={customImg || '/icons/profile/wallet-cashback-login-icon.svg'}
              textColor={theme.palette.primary.main}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {/*
                Purely a visual affordance: the whole row is the control (the
                ListItemButton above carries onClick), and this Button has no
                handler of its own. Left interactive it was a focusable element
                that did nothing, nested inside a link, with no accessible name
                — MUI's SvgIcon is aria-hidden by default, so its only child
                contributed no text.
              */}
              <Button
                variant='contained'
                aria-hidden
                tabIndex={-1}
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '100%',
                  minWidth: 'unset',
                  minHeight: 'unset',
                  backgroundColor: 'white',
                }}
              >
                {isRtl ? (
                  <KeyboardArrowLeftIcon sx={ArrowIConSx} />
                ) : (
                  <KeyboardArrowRightIcon sx={ArrowIConSx} />
                )}
              </Button>
            </Box>
          </Stack>
        </ListItemButton>
      </ListItem>
    </Container>
  );
};

export default WalletCashbackInfoBadge;
